import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { scroller } from "react-scroll";

export default function Navbar() {
  const [t, i18n] = useTranslation("global");
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isNavOpen, setIsNavOpen] = useState(false);

  const changeLanguage = (lng) => {
    localStorage.setItem("language", lng);
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, [i18n]);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsNavOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const handleMenuItemClick = (action) => {
    if (action) action();
    // Close mobile menu after clicking
    if (window.innerWidth < 1024) {
      setIsNavOpen(false);
    }
  };

  const scrollToSection = (id) => {
    scroller.scrollTo(id, {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
    });
  };

  return (
    <nav className="bg-gray-900 border-b-4 border-[#fcab21] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <NavLink to="/" className="flex items-center">
              <img
                src={
                  new URL("../../assets/images/logo.png", import.meta.url).href
                }
                alt="Flyxom Logo"
                className="h-12 w-auto"
              />
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {isHomePage ? (
              <>
                <button
                  onClick={() => scrollToSection("flyxom")}
                  className="px-4 py-2 rounded-md text-white hover:bg-gray-800 hover:text-[#fcab21] transition-colors duration-200"
                >
                  {t("WIF.text")}
                </button>
                <button
                  onClick={() => scrollToSection("us")}
                  className="px-4 py-2 rounded-md text-white hover:bg-gray-800 hover:text-[#fcab21] transition-colors duration-200"
                >
                  {t("US.text")}
                </button>
                <NavLink
                  to="/login"
                  className="px-4 py-2 rounded-md text-white hover:bg-gray-800 hover:text-[#fcab21] transition-colors duration-200"
                >
                  {t("Login.text")}
                </NavLink>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="px-4 py-2 rounded-md text-white hover:bg-gray-800 hover:text-[#fcab21] transition-colors duration-200"
                >
                  {t("ContactH.text")}
                </button>
              </>
            ) : (
              <NavLink
                to="/"
                className="px-4 py-2 rounded-md text-white hover:bg-gray-800 hover:text-[#fcab21] transition-colors duration-200"
              >
                {t("Home.text")}
              </NavLink>
            )}

            {/* Language Switcher - Desktop */}
            <div className="flex items-center space-x-1 ml-2 border-l border-gray-700 pl-2">
              <button
                onClick={() => changeLanguage("es")}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  i18n.language === "es"
                    ? "bg-[#fcab21] text-gray-900"
                    : "text-white hover:bg-gray-800 hover:text-[#fcab21]"
                }`}
              >
                ES
              </button>
              <button
                onClick={() => changeLanguage("en")}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  i18n.language === "en"
                    ? "bg-[#fcab21] text-gray-900"
                    : "text-white hover:bg-gray-800 hover:text-[#fcab21]"
                }`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleNav}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-[#fcab21] hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#fcab21] transition-colors duration-200"
              aria-expanded={isNavOpen}
              aria-label="Toggle navigation menu"
            >
              <span className="sr-only">Open main menu</span>
              {/* Hamburger icon with animation */}
              <div className="w-6 h-5 flex flex-col justify-between">
                <span
                  className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                    isNavOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                />
                <span
                  className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-in-out ${
                    isNavOpen ? "opacity-0" : "opacity-100"
                  }`}
                />
                <span
                  className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                    isNavOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`lg:hidden transition-all duration-300 ease-in-out ${
          isNavOpen
            ? "max-h-screen opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-800 shadow-lg">
          {isHomePage ? (
            <>
              <button
                onClick={() =>
                  handleMenuItemClick(() => scrollToSection("flyxom"))
                }
                className="block w-full text-left px-4 py-3 rounded-md text-base font-medium text-white hover:bg-gray-700 hover:text-[#fcab21] transition-colors duration-200"
              >
                {t("WIF.text")}
              </button>
              <button
                onClick={() => handleMenuItemClick(() => scrollToSection("us"))}
                className="block w-full text-left px-4 py-3 rounded-md text-base font-medium text-white hover:bg-gray-700 hover:text-[#fcab21] transition-colors duration-200"
              >
                {t("US.text")}
              </button>
              <NavLink
                to="/login"
                className="block px-4 py-3 rounded-md text-base font-medium text-white hover:bg-gray-700 hover:text-[#fcab21] transition-colors duration-200"
              >
                {t("Login.text")}
              </NavLink>
              <button
                onClick={() =>
                  handleMenuItemClick(() => scrollToSection("contact"))
                }
                className="block w-full text-left px-4 py-3 rounded-md text-base font-medium text-white hover:bg-gray-700 hover:text-[#fcab21] transition-colors duration-200"
              >
                {t("ContactH.text")}
              </button>
            </>
          ) : (
            <NavLink
              to="/"
              className="block px-4 py-3 rounded-md text-base font-medium text-white hover:bg-gray-700 hover:text-[#fcab21] transition-colors duration-200"
            >
              {t("Home.text")}
            </NavLink>
          )}

          {/* Language Switcher - Mobile */}
          <div className="flex items-center space-x-2 px-4 pt-4 pb-2 border-t border-gray-700 mt-2">
            <span className="text-sm text-gray-400 mr-2">Language:</span>
            <button
              onClick={() => handleMenuItemClick(() => changeLanguage("es"))}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                i18n.language === "es"
                  ? "bg-[#fcab21] text-gray-900"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              Español
            </button>
            <button
              onClick={() => handleMenuItemClick(() => changeLanguage("en"))}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                i18n.language === "en"
                  ? "bg-[#fcab21] text-gray-900"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              English
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
